import type { MouseEvent } from 'react';
import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Theme } from '@material-ui/core';
import {
    Divider,
    ListItem,
    ListItemText,
    ListSubheader,
    Typography,
} from '@material-ui/core';
import { showToast } from '@klpkonsern/ui';

import {
    Block,
    IconName,
    Illustration,
    useKlpStyles,
    ConfirmationDialog,
} from '@klp/design';
import { ProsjektRoutes } from '@klp/prosjekt/config';
import { useDeleteProsjektProcessMutation } from '../../services/rtkApi';

export type MenuPopperDeleteProjectProps = {
    canDelete: boolean;
    prosessInstansId: string | undefined;
    navigateOnDelete: boolean;
    onClose?: () => void;
};

const styles = (theme: Theme) => ({
    redText: {
        color: 'red',
    },

    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        paddingRight: theme.spacing(2),
    },
});
export const MenuPopperDeleteProject = ({
    canDelete,
    prosessInstansId,
    navigateOnDelete,
    onClose,
}: MenuPopperDeleteProjectProps) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const history = useHistory();
    const anchorRef = useRef(null);

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const [deleteProsjekt, { isLoading }] = useDeleteProsjektProcessMutation();

    const handleDeleteProsjekt = async (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        setShowConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!prosessInstansId) return;

        await deleteProsjekt({ prosessInstansId })
            .unwrap()
            .then(() => {
                setShowConfirmDialog(false);
                onClose?.();
                showToast({
                    body: t('prosjekt.successfulOperation'),
                    type: 'success',
                });

                if (navigateOnDelete) {
                    history.push(ProsjektRoutes.projectSchema());
                }
            })
            .catch(() => {
                setShowConfirmDialog(false);
                onClose?.();
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
    };

    const handleCancelDelete = () => {
        setShowConfirmDialog(false);
    };

    if (!canDelete) return null;

    return (
        <>
            <Divider />
            <ListSubheader disableSticky={true}>
                <Typography>{t('general.delete')}</Typography>
            </ListSubheader>
            <ListItem
                dense={true}
                button={true}
                onClick={handleDeleteProsjekt}
                disabled={isLoading}
                ref={anchorRef}
            >
                <Block className={classes.iconContainer}>
                    <Illustration icon={IconName.DELETE} />
                </Block>
                <ListItemText>
                    <Typography className={classes.redText}>
                        {t('prosjekt.deleteProject.title')}
                    </Typography>
                </ListItemText>
            </ListItem>

            <ConfirmationDialog
                anchorRef={anchorRef}
                showDialog={showConfirmDialog}
                dialogContent={t('prosjekt.deleteProject.confirmationText')}
                confirmText={t('general.deleteYes')}
                cancelText={t('general.cancel')}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                isLoading={isLoading}
                className="withBlur"
            />
        </>
    );
};
